import type { WorkflowPlan } from '../../store/chat';
import { CubeIcon, SpinnerIcon, RocketIcon, CheckIcon } from '../ui/icons';

interface PlanCardProps {
  plan: WorkflowPlan;
  onBuild: () => void;
  isBuilding: boolean;
  isBuilt: boolean;
}

export function PlanCard({ plan, onBuild, isBuilding, isBuilt }: PlanCardProps) {
  return (
    <div className={`overflow-hidden rounded-xl border ${isBuilt ? 'border-green-200 bg-white' : 'border-primary-200 bg-gradient-to-br from-primary-50 to-white'}`}>
      {/* Header */}
      <div className={`flex items-center gap-3 border-b ${isBuilt ? 'border-green-200 bg-green-50' : 'border-primary-100'} px-4 py-3`}>
        <div className={`flex h-8 w-8 items-center justify-center rounded-lg ${isBuilt ? 'bg-green-100' : 'bg-primary-100'}`}>
          {isBuilt ? (
            <CheckIcon className="h-4 w-4 text-green-600" />
          ) : (
            <CubeIcon className="h-4 w-4 text-primary-600" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className={`text-sm font-semibold ${isBuilt ? 'text-green-800' : 'text-gray-900'}`}>{plan.title}</h3>
          <p className={`text-xs ${isBuilt ? 'text-green-600' : 'text-gray-500'}`}>{plan.steps.length} steps</p>
        </div>
        {isBuilt && (
          <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-semibold text-green-700">
            <CheckIcon className="h-3 w-3" />
            Accepted
          </span>
        )}
      </div>

      {/* Summary */}
      <div className="px-4 py-3">
        <p className="text-sm text-gray-600">{plan.summary}</p>
      </div>

      {/* Steps */}
      <div className={`border-t ${isBuilt ? 'border-green-100' : 'border-primary-100'} px-4 py-3`}>
        <h4 className="mb-2 text-xs font-medium uppercase tracking-wider text-gray-500">
          Workflow Steps
        </h4>
        <ol className="space-y-2">
          {plan.steps.map((step) => (
            <li key={step.stepNumber} className="flex items-start gap-2">
              <span className={`mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full text-xs font-medium ${isBuilt ? 'bg-green-100 text-green-700' : 'bg-primary-100 text-primary-700'}`}>
                {step.stepNumber}
              </span>
              <div className="flex-1 min-w-0">
                <span className="text-sm font-medium text-gray-900">
                  {step.nodeName}
                </span>
                <p className="text-xs text-gray-500">{step.description}</p>
              </div>
            </li>
          ))}
        </ol>
      </div>

      {/* Required Credentials */}
      {plan.requiredCredentials && plan.requiredCredentials.length > 0 && (
        <div className={`border-t ${isBuilt ? 'border-green-100' : 'border-primary-100'} px-4 py-3`}>
          <h4 className="mb-2 text-xs font-medium uppercase tracking-wider text-gray-500">
            Required Credentials
          </h4>
          <div className="flex flex-wrap gap-1.5">
            {plan.requiredCredentials.map((cred, index) => (
              <span
                key={index}
                className="inline-flex items-center rounded-md bg-amber-50 px-2 py-1 text-xs font-medium text-amber-700 ring-1 ring-inset ring-amber-600/20"
              >
                {cred}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Build Button */}
      {!isBuilt && (
        <div className={`border-t ${isBuilt ? 'border-green-100' : 'border-primary-100'} px-4 py-3`}>
          <button
            onClick={onBuild}
            disabled={isBuilding}
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isBuilding ? (
              <>
                <SpinnerIcon className="h-4 w-4" />
                Building workflow...
              </>
            ) : (
              <>
                <RocketIcon className="h-4 w-4" />
                Begin building
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
}
